import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader/PageHeader';
import StepIndicator from '../../components/StepIndicator/StepIndicator';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';
import useBrandRegistrationService from '../../app/services/hooks/useBrandRegistrationService';
import useBrandRegistrationStagesService from '../../app/services/hooks/useBrandRegistrationStagesService';
import { BrandRegistrationApplication } from '../../app/models/BrandRegistration';
import { StagesData } from '../../app/services/hooks/useBrandRegistrationStagesService';
import ApplicationStep from './steps/ApplicationStep';
import VerificationStep from './steps/VerificationStep';
import InapiApplicationStep from './steps/InapiApplicationStep';
import FormalExaminationStep from './steps/FormalExaminationStep';
import OfficialPublicationStep from './steps/OfficialPublicationStep';
import SubstantiveExaminationStep from './steps/SubstantiveExaminationStep';
import DefinitiveResolutionStep from './steps/DefinitiveResolutionStep';
import '../../styles/page-components.css';

interface RouteParams {
    id: string;
    step: string;
}

const BrandRegistrationEdit: React.FC = () => {
    const { id, step } = useParams<RouteParams>();
    const history = useHistory();
    const currentStep = parseInt(step || '1');
    
    const { showBrandRegistration, fetchingShowBrandRegistration } = useBrandRegistrationService();
    const { getBrandRegistrationStages, fetchingGetBrandRegistrationStages } = useBrandRegistrationStagesService();
    
    const [applicationData, setApplicationData] = useState<BrandRegistrationApplication | null>(null);
    const [stagesData, setStagesData] = useState<StagesData | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (id) {
            loadData();
        }
    }, [id]);

    const loadData = () => {
        // Cargar datos de la aplicación
        showBrandRegistration(parseInt(id), {
            onSuccess: (response) => {
                if (response.data && response.data.application) {
                    setApplicationData(response.data.application);
                }
            },
            onError: (response) => {
                toast.error(response.message || 'Error al cargar la solicitud');
                setLoading(false);
            }
        });

        // Cargar datos de las etapas
        getBrandRegistrationStages(parseInt(id), {
            onSuccess: (response) => {
                if (response.data) {
                    setStagesData(response.data);
                }
                setLoading(false);
            },
            onError: (response) => {
                toast.error(response.message || 'Error al cargar las etapas');
                setLoading(false);
            }
        });
    };

    const handleStepChange = (newStep: number) => {
        // Verificar si la etapa está disponible
        if (stagesData) {
            const stage = stagesData.stages.find(s => s.stage_number === newStep);
            if (stage && (stage.status === 'completed' || stage.status === 'current')) {
                history.push(`/intranet/brand-registration/edit/${id}/step/${newStep}`);
            } else {
                toast.warning(stage?.blocked_reason || 'Esta etapa no está disponible todavía');
            }
        }
    };

    const handleNext = () => {
        if (currentStep < 7) {
            handleStepChange(currentStep + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            handleStepChange(currentStep - 1);
        } else {
            history.push(`/intranet/brand-registration/stages/${id}`);
        }
    };

    // Mostrar loading mientras se cargan los datos
    if (loading || fetchingShowBrandRegistration || fetchingGetBrandRegistrationStages) {
        return (
            <div className="container-fluid">
                <div className="page-container">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Cargando...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Si no hay datos, mostrar error
    if (!applicationData || !stagesData) {
        return (
            <div className="container-fluid">
                <div className="page-container">
                    <div className="alert alert-danger" role="alert">
                        No se pudieron cargar los datos de la solicitud.
                    </div>
                </div>
            </div>
        );
    }

    // Obtener información de la etapa actual
    const currentStageInfo = stagesData.stages.find(s => s.stage_number === currentStep);
    
    // Verificar si el usuario puede editar la etapa actual
    // Solo puede editar si:
    // 1. La etapa está 'current' o 'completed' (disponible)
    // 2. El usuario tiene el permiso requerido (can_edit === true del backend)
    const isStageAvailable = currentStageInfo?.status === 'current' || currentStageInfo?.status === 'completed';
    const hasPermission = currentStageInfo?.can_edit === true;
    const canSign = currentStageInfo?.can_sign === true;
    const canConfirm = currentStageInfo?.can_confirm === true;
    const isStageEditable = isStageAvailable && hasPermission;

    // Preparar steps para el indicador (sin "Etapa X.")
    const steps = stagesData.stages.map(stage => ({
        number: stage.stage_number,
        title: stage.title.replace(/^Etapa \d+\.\s*/i, ''), // Quitar "Etapa X." del inicio
        status: stage.status
    }));

    return (
        <div className="container-fluid">
            <div className="page-container">
                <PageHeader
                    breadcrumbs={[
                        { label: 'Inicio', path: '/intranet' },
                        { label: 'Registro de marca', path: '/intranet/brand-registration' },
                        { label: 'Etapas', path: `/intranet/brand-registration/stages/${id}` },
                        { label: currentStageInfo?.title || `Etapa ${currentStep}`, isCurrent: true }
                    ]}
                    title={currentStageInfo?.title || `Etapa ${currentStep}`}
                    description={currentStageInfo?.description || ''}
                />

                {/* Indicador de pasos */}
                <div className="mb-4">
                    <StepIndicator
                        steps={steps}
                        currentStep={currentStep}
                        onStepClick={handleStepChange}
                    />
                </div>

                {/* Información de progreso */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row align-items-center">
                            <div className="col-md-3">
                                <div className="mb-2">
                                    <small className="text-muted">ID Solicitud</small>
                                    <div className="fw-bold">{stagesData.summary.id}</div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="mb-2">
                                    <small className="text-muted">Abogado Asignado</small>
                                    <div className="fw-bold">{stagesData.summary.assigned_lawyer}</div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="mb-2">
                                    <small className="text-muted">Etapas Completadas</small>
                                    <div className="fw-bold">{stagesData.completed_stages} de {stagesData.total_stages}</div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <small className="text-muted d-block mb-1">Progreso</small>
                                <div className="d-flex align-items-center gap-2">
                                    <div className="progress flex-grow-1" style={{ height: '8px' }}>
                                        <div 
                                            className="progress-bar bg-primary" 
                                            role="progressbar" 
                                            style={{ 
                                                width: `${Math.round((stagesData.completed_stages / stagesData.total_stages) * 100)}%`
                                            }}
                                            aria-valuenow={Math.round((stagesData.completed_stages / stagesData.total_stages) * 100)} 
                                            aria-valuemin={0} 
                                            aria-valuemax={100}
                                        >
                                        </div>
                                    </div>
                                    <span className="fw-bold text-primary" style={{ minWidth: '40px', fontSize: '0.9rem' }}>
                                        {Math.round((stagesData.completed_stages / stagesData.total_stages) * 100)}%
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contenido de la etapa */}
                <div className="card">
                    <div className="card-body">
                        {/* Alerta cuando la etapa está bloqueada (pending) */}
                        {currentStageInfo?.status === 'pending' && currentStep === 2 && (
                            <div className="alert alert-info border-info mb-4">
                                <div className="d-flex align-items-start">
                                    <i className="fas fa-info-circle me-3 mt-1"></i>
                                    <div>
                                        <strong>Etapa bloqueada</strong>
                                        <p className="mb-0 mt-1">
                                            Esta etapa requiere que se asigne un abogado a la solicitud. Un administrador debe asignar un abogado antes de continuar con la verificación de factibilidad.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Alerta cuando el usuario no tiene permisos (no mostrar si puede firmar) */}
                        {isStageAvailable && !hasPermission && !canSign && !canConfirm && (
                            <div className="alert alert-warning border-warning mb-4">
                                <div className="d-flex align-items-start">
                                    <i className="fas fa-exclamation-triangle me-3 mt-1"></i>
                                    <div>
                                        <strong>Permisos insuficientes</strong>
                                        <p className="mb-0 mt-1">
                                            No tiene permisos para editar esta etapa. Por favor, contacte con un administrador si necesita acceso.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {currentStep === 1 && (
                            <ApplicationStep 
                                applicationData={applicationData}
                                isEditable={isStageEditable}
                                onUpdate={loadData}
                            />
                        )}
                        {currentStep === 2 && currentStageInfo?.status !== 'pending' && (
                            <VerificationStep
                                brandRegistrationId={parseInt(id)}
                                applicationData={applicationData}
                                isEditable={isStageEditable}
                                onUpdate={loadData}
                                paymentManagedByFirm={!!stagesData.summary.payment_managed_by_firm}
                            />
                        )}
                        {currentStep === 3 && (
                            <InapiApplicationStep 
                                brandRegistrationId={parseInt(id)}
                                stagesData={stagesData}
                                isEditable={isStageEditable}
                                canSign={canSign}
                                canConfirm={canConfirm}
                                onUpdate={loadData}
                            />
                        )}
                        {currentStep === 4 && (
                            <FormalExaminationStep 
                                brandRegistrationId={parseInt(id)}
                                stagesData={stagesData}
                                isEditable={isStageEditable}
                                onUpdate={loadData}
                            />
                        )}
                        {currentStep === 5 && (
                            <OfficialPublicationStep 
                                brandRegistrationId={parseInt(id)}
                                stagesData={stagesData}
                                isEditable={isStageEditable}
                                onUpdate={loadData}
                            />
                        )}
                        {currentStep === 6 && (
                            <SubstantiveExaminationStep 
                                brandRegistrationId={parseInt(id)}
                                onUpdate={loadData}
                                isEditable={isStageEditable}
                            />
                        )}
                        {currentStep === 7 && (
                            <DefinitiveResolutionStep
                                brandRegistrationId={parseInt(id)}
                                onUpdate={loadData}
                                isEditable={isStageEditable}
                                paymentManagedByFirm={!!stagesData.summary.payment_managed_by_firm}
                            />
                        )}
                    </div>
                </div>

                {/* Botones de navegación - Solo visibles si tiene permisos de edición */}
                {isStageEditable && (
                    <div className="d-flex justify-content-between mt-4 mb-5">
                        <ButtonGeneral
                            type="button"
                            variant="outline"
                            size="large"
                            onClick={handleBack}
                            icon="fas fa-arrow-left"
                            iconPosition="left"
                            title={currentStep === 1 ? 'Volver a etapas' : 'Anterior'}
                        />

                        {currentStep < 7 && (
                            <ButtonGeneral
                                type="button"
                                variant="primary"
                                size="large"
                                onClick={handleNext}
                                icon="fas fa-arrow-right"
                                iconPosition="right"
                                title="Siguiente"
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BrandRegistrationEdit;

